import type { MaybeRefOrGetter, Ref } from 'vue';

import { ref, toValue } from 'vue';
import { enAU } from 'date-fns/locale';

import { getWeekDays, getWeekStart } from '@/utils/date';

export interface PickerFieldComposable {
  pickerAttrs: object;
  handleAllEnterKeydown: (event: Event) => void;
  handleFocus: (event: Event, isMenuOpen: boolean) => void;
  handleClick: (event: Event, isMenuOpen: boolean) => void;
  handleAllEscKeydown: (event: Event) => void;
  handleAppendIconClick: (event: Event, isMenuOpen: boolean) => void;
  handleAllSpaceKeydown: (event: Event) => void;
  handleDownKeydown: (event: Event, isMenuOpen: boolean) => void;
}

export default function usePickerField(
  root: Ref<HTMLElement | null>,
  datepickerEl: Ref<{
    selectDate: () => void;
    openMenu: () => void;
    closeMenu: () => void;
  } | null>,
  readonly: MaybeRefOrGetter<boolean> = false,
): PickerFieldComposable {
  const noOpenOnFocus = ref(false);

  function getInput(): HTMLInputElement | null {
    if (root.value == null) return root.value;
    return root.value.querySelector('input');
  }

  function handleAllEnterKeydown(event: Event): void {
    const inputEl = getInput();
    datepickerEl.value?.selectDate(); // select and close
    if (inputEl !== null && document.activeElement !== inputEl) {
      noOpenOnFocus.value = true;
      inputEl.focus();
      event.preventDefault(); // will submit form otherwise
    }
  }

  function handleFocus(event: Event, isMenuOpen: boolean): void {
    if (toValue(readonly)) return;
    if (noOpenOnFocus.value) {
      noOpenOnFocus.value = false;
      return;
    }
    datepickerEl.value?.openMenu();
  }

  function handleClick(event: Event, isMenuOpen: boolean): void {
    if (isMenuOpen) {
      event.stopPropagation();
    } else {
      noOpenOnFocus.value = true;
    }
  }

  function handleAllEscKeydown(event: Event): void {
    datepickerEl.value?.closeMenu();
    const inputEl = getInput();
    if (inputEl !== null && document.activeElement !== inputEl) {
      noOpenOnFocus.value = true;
      inputEl.focus();
    }
  }

  function handleAppendIconClick(event: Event, isMenuOpen: boolean): void {
    if (toValue(readonly)) return;
    // it automatically sets cursor pointer to the icon
    // TODO troubles with toggle and svg shape clicks
    noOpenOnFocus.value = true;
    setTimeout(() => {
      datepickerEl.value?.openMenu();
    }, 100);
  }

  function handleAllSpaceKeydown(event: Event): void {
    const inputEl = getInput();
    if (inputEl !== null && document.activeElement !== inputEl) {
      // disable page scroll by space when calendar is focused
      event.preventDefault();
    }
  }

  function handleDownKeydown(event: Event, isMenuOpen: boolean): void {
    if (toValue(readonly)) return;
    if (isMenuOpen) {
      const cal = root.value?.querySelector('.dp--menu') as HTMLElement | null;
      if (cal != null) {
        cal.focus();
      }
    } else {
      datepickerEl.value?.openMenu();
    }
  }

  const calendarLocale = enAU;

  const dayNames = getWeekDays({ locale: calendarLocale }, 'EEE');
  const weekStart = getWeekStart({ locale: calendarLocale });

  const pickerAttrs = {
    'input-attrs': { clearable: false, hideInputIcon: true },
    'arrow-navigation': true,
    'config': {
      escClose: false,
      spaceConfirm: false,
      monthChangeOnScroll: false,
    },
    'locale': calendarLocale,
    'day-names': dayNames,
    'week-start': weekStart,
    'action-row': {
      showSelect: true,
      showCancel: true,
      showNow: false,
      showPreview: true,
    },
    'six-weeks': true,
    'formats': { month: 'MMMM' },
  };

  return {
    pickerAttrs,
    handleAllEnterKeydown,
    handleFocus,
    handleClick,
    handleAllEscKeydown,
    handleAppendIconClick,
    handleAllSpaceKeydown,
    handleDownKeydown,
  };
}
